import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Check, ArrowRight, Phone } from "lucide-react";
import { CLINIC } from "../lib/api";
import TrustBar from "../components/TrustBar";
import ServicesBento from "../components/ServicesBento";
import Differentiators from "../components/Differentiators";
import TeamSection from "../components/TeamSection";
import TestimonialsSection from "../components/TestimonialsSection";
import FAQ from "../components/FAQ";
import CTASection from "../components/CTASection";

const FAQ_ITEMS = [
  {
    q: "¿La primera visita es realmente gratuita?",
    a: "Sí, la primera visita es 100% gratuita y sin compromiso. Incluye revisión completa, diagnóstico y presupuesto personalizado. Si fuera necesario un TAC 3D, también va incluido.",
  },
  {
    q: "¿Ofrecen financiación para los tratamientos?",
    a: "Sí, ofrecemos financiación a medida de hasta 60 meses sin intereses. Adaptamos las cuotas a tu capacidad económica para que puedas realizar tu tratamiento con total tranquilidad.",
  },
  {
    q: "¿Atienden urgencias dentales el mismo día?",
    a: "Sí. Si llamas antes de las 12:00 te atendemos el mismo día. Fuera de horario, contamos con servicio de urgencias dentales para Calafell y alrededores.",
  },
  {
    q: "¿Puedo venir de visita si estoy de vacaciones en Calafell?",
    a: "Por supuesto. Atendemos a muchos pacientes turistas, tanto nacionales como internacionales. Tenemos personal que habla español, catalán, inglés y francés.",
  },
  {
    q: "¿Dónde estáis ubicados?",
    a: `Estamos en ${CLINIC.address}, ${CLINIC.city}, junto a la estación Renfe y a pocos metros de la playa. Fácil acceso y aparcamiento cerca.`,
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-teal-50/50 to-white pt-16 pb-20 lg:pt-24 lg:pb-28" data-testid="hero-section">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-40 pointer-events-none">
          <div className="absolute top-20 right-20 w-96 h-96 bg-orange-200 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-40 w-72 h-72 bg-amber-100 rounded-full blur-3xl" />
        </div>

        <div className="container-max relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              {/* Trust badge */}
              <div className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full px-4 py-2 mb-6 shadow-sm">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <span className="text-xs font-semibold text-slate-700">
                  4.9/5 · +500 pacientes satisfechos
                </span>
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-xs font-semibold text-teal-700">40+ años en Calafell</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[4rem] font-bold text-slate-900 leading-[1.05] tracking-tight mb-6">
                Especialistas en <span className="underline-accent">implantes</span>, ortodoncia y estética dental
              </h1>

              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-8">
                Somos la clínica dental de referencia en Calafell Platja. Más de 40 años
                de experiencia en implantes, ortodoncia invisible, estética y odontopediatría.
                Con la <strong className="text-slate-900">primera visita gratuita</strong>.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Link
                  to="/contacto"
                  data-testid="hero-cta-primary"
                  className="btn-primary justify-center text-base px-8 py-4"
                >
                  Pide tu primera visita gratis
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`tel:${CLINIC.phone}`}
                  data-testid="hero-cta-phone"
                  className="btn-secondary justify-center text-base px-8 py-4"
                >
                  <Phone className="w-4 h-4" />
                  {CLINIC.phoneDisplay}
                </a>
              </div>

              <div className="flex flex-wrap gap-5 text-sm text-slate-600">
                {["Primera visita gratuita", "Financiación sin intereses", "Urgencias mismo día"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center">
                      <Check className="w-3 h-3 text-teal-700" strokeWidth={3} />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_clinica-calafell-v2/artifacts/wkh17088_20210226_150059.jpg"
                  alt="Clínica Dental Pina Calafell"
                  className="w-full h-[520px] object-cover rounded-[2rem] shadow-xl"
                />
                {/* Floating card */}
                <div className="absolute -left-6 bottom-10 bg-white rounded-2xl shadow-xl border border-slate-100 p-5 max-w-[240px]">
                  <div className="flex gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-700 font-medium leading-snug mb-2">
                    "Profesionales como pocos. 20 años confiando en ellos."
                  </p>
                  <div className="text-xs text-slate-500">— María G., Calafell</div>
                </div>
                <div className="absolute -right-4 top-10 bg-amber-400 text-slate-900 rounded-2xl shadow-xl p-4 font-bold text-sm">
                  <div className="text-2xl">40+</div>
                  <div className="text-xs font-medium opacity-80">años de experiencia</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <TrustBar />
      <ServicesBento />
      <Differentiators />
      <TeamSection />
      <TestimonialsSection />

      {/* Emergency banner */}
      <section className="py-12 bg-amber-50 border-y border-amber-200" data-testid="emergency-banner">
        <div className="container-max">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="relative w-4 h-4 rounded-full bg-amber-500 pulse-dot shrink-0" />
              <div>
                <div className="font-bold text-slate-900 text-lg">¿Tienes una urgencia dental?</div>
                <div className="text-sm text-slate-700">Te atendemos hoy mismo. Llama ahora.</div>
              </div>
            </div>
            <div className="flex gap-3">
              <a href={`tel:${CLINIC.phone}`} className="btn-primary" data-testid="emergency-phone">
                <Phone className="w-4 h-4" /> {CLINIC.phoneDisplay}
              </a>
              <Link to="/urgencias-dentales-calafell" className="btn-secondary">
                Más info
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FAQ items={FAQ_ITEMS} />
      <CTASection />
    </div>
  );
}
